import type { JSX } from 'react';
import { formatExperienceHeadline } from '../utils/experience-period';
import type { ContactsByExperience } from '../utils/contact-results';
import type { Experience } from '@/features/experiences/types/experience.types';
import type { Verification } from '@/features/verifications/types/verification.types';

interface CandidateReviewSummaryProps {
  experiences: Experience[];
  contactsByExperience: ContactsByExperience;
}

// A missing entry means the contact query failed or has not resolved, which is
// not the same as an experience without reference contacts.
function ContactList({ contacts }: { contacts: Verification[] | undefined }): JSX.Element {
  if (!contacts) {
    return <p className="text-xs text-destructive">Could not load reference contacts</p>;
  }

  if (contacts.length === 0) {
    return <p className="text-xs text-muted-foreground">No reference contact added</p>;
  }

  return (
    <ul className="space-y-1">
      {contacts.map((contact) => (
        <li key={contact.id} className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{contact.refereeName}</span>{' '}
          <span>{contact.refereeEmail}</span>
        </li>
      ))}
    </ul>
  );
}

function ExperienceReview({
  experience,
  contacts,
}: {
  experience: Experience;
  contacts: Verification[] | undefined;
}): JSX.Element {
  return (
    <div className="space-y-1 rounded border p-3 text-left">
      <p className="text-sm font-medium">{formatExperienceHeadline(experience)}</p>
      <ContactList contacts={contacts} />
    </div>
  );
}

export function CandidateReviewSummary({
  experiences,
  contactsByExperience,
}: CandidateReviewSummaryProps): JSX.Element {
  if (experiences.length === 0) {
    return <p className="text-sm text-muted-foreground">No work experience added yet.</p>;
  }

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Experience and reference contacts</h3>
      {experiences.map((experience) => (
        <ExperienceReview
          key={experience.id}
          experience={experience}
          contacts={contactsByExperience[experience.id]}
        />
      ))}
    </section>
  );
}
